import { Grid } from '@material-ui/core';
import moment from 'moment';
import React, { useMemo, useContext, useEffect, useState } from 'react';
import { required, SelectInput, TextInput } from 'react-admin';
import { FormSpy } from 'react-final-form';
import DateInput from '../../../core/components/forked/input/DateInput';
import { isValidDate, formatDate } from '../../../core/utils/index';
import setFieldTouched from 'final-form-set-field-touched';
import { CopyInvestorContext } from './CopyInvestorContext';
import DocumentApi from '../../../core/apis/DocumentApi';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const translateWatermarkRecordToInput = v => {
  if (v === null) {
    return -1;
  }
  return v;
};

const DetailsSummary = props => {
  const { batchLookups, formApi } = props;
  const allSubcategories = useMemo(
    () => (batchLookups && batchLookups.allSubcategories) || [],
    [batchLookups]
  );
  const [useWatermark, setUseWatermark] = useState(false);
  const [subcategories, setSubcategories] = useState([]);

  // This two effet and the "change" after should be refactor slightly to avoid this ugly code duplication
  useEffect(() => {
    if (formApi) {
      const documentValue = formApi.getState().values.document;
      const subcategories =
        allSubcategories && allSubcategories.length > 0
          ? allSubcategories.filter(
              cat => cat.categoryId === documentValue.categoryId
            )
          : [];
      setSubcategories(subcategories);
    }
  }, [formApi, allSubcategories]);

  useEffect(() => {
    if (formApi) {
      const documentValue = formApi.getState().values.document;

      const batchSecurity =
        batchLookups.allSecurities && batchLookups.allSecurities.length > 0
          ? batchLookups.allSecurities.find(
              sec => sec.id === documentValue.documentSecurityId
            )
          : null;
      const useWatermark = batchSecurity && batchSecurity.applyWatermark;
      setUseWatermark(useWatermark);
      if (!useWatermark) {
        formApi.change(`document.watermarkId`, null);
      }
    }
  }, [formApi, batchLookups.allSecurities]);

  const onCategoryChange = () => {
    if (formApi) {
      const documentValue = formApi.getState().values.document;
      const subcategories =
        allSubcategories && allSubcategories.length > 0
          ? allSubcategories.filter(
              cat => cat.categoryId === documentValue.categoryId
            )
          : [];
      setSubcategories(subcategories);
      if (subcategories.length === 0) {
        formApi.change(`document.subcategoryId`, 0);
      }
    }
  };

  const onSecurityChange = value => {
    const batchSecurity =
      batchLookups.allSecurities && batchLookups.allSecurities.length > 0
        ? batchLookups.allSecurities.find(sec => sec.id === value)
        : null;
    const useWatermark = batchSecurity && batchSecurity.applyWatermark;
    setUseWatermark(useWatermark);
    if (!useWatermark) {
      formApi.change(`document.watermarkId`, null);
    }
  };

  //add logic to hide unhide Watermark dropdown

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={12} md={4}>
        <TextInput
          label="Document Title"
          source="document.documentTitle"
          variant={props.variant}
          validate={required()}
          alwaysOn
          multiline
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <TextInput
          source="document.status"
          variant={props.variant}
          label="Document Status"
          disabled
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <DateInput
          label="Document Date"
          source="document.documentDate"
          validate={[required(), isValidDate]}
          placeholder="MM/dd/yyyy"
          customParseValue={formatDate}
          customOnChange={() => {
            //set Due Date touched to true so validation error is displayed if any
            formApi.mutators.setFieldTouched('document.dueDate', true);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <SelectInput
          source="document.categoryId"
          label="Document Category"
          optionText="name"
          choices={batchLookups.allCategories}
          //allowEmpty
          variant={props.variant}
          helperText={false}
          onChange={onCategoryChange}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <SelectInput
          source="document.documentSecurityId"
          label="Security"
          optionText="description"
          choices={batchLookups.allSecurities}
          variant={props.variant}
          validate={required()}
          helperText={false}
          onChange={event => {
            onSecurityChange(event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <DateInput
          label="Due Date"
          source="document.dueDate"
          placeholder="MM/dd/yyyy"
          validate={[isValidDate]}
          customParseValue={formatDate}
          helperText={false}
        />
      </Grid>
      {subcategories.length > 0 ? (
        <Grid item xs={12} sm={12} md={4}>
          <SelectInput
            source="document.subcategoryId"
            label="Document Subcategory"
            optionText="name"
            choices={subcategories}
            allowEmpty
            variant={props.variant}
            helperText={false}
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={4}></Grid>
      )}
      {useWatermark ? (
        <Grid item xs={12} sm={12} md={4}>
          <SelectInput
            source="document.watermarkId"
            label="Watermark"
            optionText="profileName"
            choices={[
              {
                profileName: 'Default Profile',
                id: 0
              },
              ...batchLookups.allWatermarks
            ]}
            allowEmpty={false}
            variant={props.variant}
            defaultValue={0}
            format={translateWatermarkRecordToInput}
            helperText={false}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

const PropertiesStep = props => {
  const { investorContext, setInvestorsContext } = useContext(
    CopyInvestorContext
  );
  const { onSubmitEnd } = props;
  const notifyServerError = useNotifyServerError();

  const createNewDocuments = async values => {
    const document = values.document;
    const copyObject = {
      investorIds: investorContext.investors,
      documentTitle: document.documentTitle,
      dueDate: document.dueDate,
      documentDate: document.documentDate,
      categoryId: document.categoryId,
      subcategoryId: document.subcategoryId,
      documentSecurityId: document.documentSecurityId,
      watermarkId: document.watermarkId
    };

    try {
      const newDocuments = await DocumentApi.copyToAnotherInvestors(
        document.id,
        copyObject
      );
      onSubmitEnd(newDocuments);
    } catch (error) {
      notifyServerError(error);
    }
  };

  const validateDates = values => {
    const errors = { document: {} };
    if (values.document.documentDate && values.document.dueDate) {
      if (
        moment(values.document.documentDate).startOf('day') >=
        moment(values.document.dueDate).startOf('day')
      ) {
        errors.document.dueDate = 'Due Date must be after Document Date';
      }
    }
    return errors;
  };

  return (
    <CustomSimpleForm
      save={createNewDocuments}
      initialValues={{
        document: {
          ...props.sourceDocument,
          status: 'Pending'
        }
      }}
      mutators={{ setFieldTouched }}
      toolbar={null}
      resource={props.resource}
      variant="standard"
      margin="normal"
      validate={validateDates}
      // //in case state is changed prevent resetting values to initialValues
      // //for example if batch had subcategory and we change category with no subcategories
      // //when we submit form subcateogry is filled with initial value, same for watermark
      // initialValuesEqual={() => true}
    >
      <FormSpy subscription={{ pristine: true }}>
        {formprops => {
          if (investorContext.currentForm === undefined) {
            setInvestorsContext({
              ...investorContext,
              currentForm: formprops.form
            });
          }

          return (
            <DetailsSummary
              {...props}
              //hasSubcategories={hasSubcategories}
              //subcategories={subcategories}
              //hasWatermark={hasWatermark}
              //onSecurityChange={this.onSecurityChange}
              formApi={investorContext.currentForm}
            />
          );
        }}
      </FormSpy>
    </CustomSimpleForm>
  );
};

export default PropertiesStep;
